import CrystalDetails from 'components/crystal/crystalDetails';
import CrystalModal from 'components/crystal/crystalModal';
import RMTable from 'components/customComponents/Controls/RMTable';
import { Input } from 'components/input';
import React, { useCallback, useMemo, useState } from 'react';
import { useRMDispatch } from 'redux/hooks';
import {
  setSelectedAssignedCrystalIds,
  setSelectedCrystalId,
} from 'redux/reducers/synchrotronTripReducer';
import { tableColumns, TableColumns } from './utils';
import { CrystalDataDTO } from './constants';
import { useIsCrystalAssignmentsDirty } from 'redux/hooks/synchrotron';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import RMModal, { RMModalProps } from 'components/modal/RMModal';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';

interface Props {
  crystals?: CrystalDataDTO[];
  handleCollected?: (id: number) => void;
  unassignCrysal?: (ids: number[]) => void;
  refetchSynchTrip?: () => void;
  isReadOnly?: boolean;
}

const AssignedCrystals = (props: Props) => {
  const { crystals, handleCollected, unassignCrysal, refetchSynchTrip, isReadOnly } =
    props;
  const dispatch = useRMDispatch();
  const [modalDetails, setModalDetails] = useState({
    showModal: false,
    body: null,
  });
  const [selectedRows, setSelectedRows] = useState<number[]>();
  const tableColumns_: TableColumns[] = tableColumns;
  const isCrystalAssignmentsDirty = useIsCrystalAssignmentsDirty();
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [modal, setModal] = useState<RMModalProps>();
  const rowsSelected = selectedRows?.length > 0;

  const closeModal = () => {
    setModalDetails({ ...modalDetails, showModal: false });
  };

  const openCrystal = (crystalId: number) => {
    setModalDetails(() => ({
      ...modalDetails,
      showModal: true,
      body: (
        <CrystalDetails
          crystalId={crystalId}
          closeModal={closeModal}
          refetchSynchTrip={refetchSynchTrip}
          editMode
          isReadOnly={isReadOnly}
        />
      ),
    }));
  };

  const handleSelectionChange = useCallback(
    (rowIndices: number[]) => {
      if (!isCrystalAssignmentsDirty) {
        setSelectedRows([...rowIndices]);
        const crystalIds = rowIndices.map((r) => {
          return crystals[r]?.id;
        });
        dispatch(setSelectedAssignedCrystalIds(crystalIds));
        dispatch(setSelectedCrystalId(crystals[rowIndices.pop()]?.id));
      } else {
        showAlertSnackBar(
          EAlertType.WARNING,
          ESnackBarPosition.TOP,
          'Please press apply to accept current crystal assignment changes or press cancel to reject it.',
        );
      }
    },
    [crystals, isCrystalAssignmentsDirty],
  );

  const handleUnassignCrystal = () => {
    unassignCrysal(selectedRows?.map((r) => crystals[r]?.id));
    setSelectedRows([]);
    setShowUnassignModal(false);
  };

  const getCollectedCheckbox = (c: CrystalDataDTO) => {
    return (
      <Input
        id="assignedcrsytal-collectedcheckbox"
        disabled={isReadOnly}
        inputType="checkbox"
        onClick={(e) => e.stopPropagation()}
        onChange={() => handleCollected(c.id)}
        checked={c.dataCollected}
        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
      />
    );
  };

  const crystalList = useMemo(
    () =>
      crystals?.map((c: CrystalDataDTO) => ({
        id: c.id,
        crystalName: c.name,
        harvestFrom: c.experimentName,
        proteinName: c.proteinName,
        wellDrop: c.wellRowCol,
        owner: c.ownerName,
        collected: getCollectedCheckbox(c),
        open: (
          <RMCustomButton
            iconProps={{
              name: 'OpenCrystal',
              subfolder: 'SynchrotronTrip',
              fill: '#FFFFFF',
            }}
            onClick={(e) => {
              e.stopPropagation();
              openCrystal(c.id);
            }}
            contentContainerClass="hover:opacity-70"
            titleTip="Crystal Info"
          />
        ),
      })),
    [crystals],
  );

  const showUnassignConfirmation = () => {
    let bodyText = 'Are you sure you want to unassign selected Crystal(s)?';
    if (crystals?.find((c) => c.result)) {
      bodyText =
        'Some of the selected Crystals have Synchrotron Trip results which will get deleted.\nAre you sure you want to continue?';
    }
    setModal((prev) => ({
      ...prev,
      body: bodyText,
      heading: `Unassign Crystals`,
      okButtonText: 'CONFIRM',
    }));
    setShowUnassignModal(true);
  };

  const handleOnRowCLick = (row) => {
    setSelectedRows([row.index]);
    dispatch(setSelectedCrystalId(crystals[row.index]?.id));
  };

  return (
    <>
      <div
        id="synchrotron-assignedcrystals-header-wrapper"
        className="flex justify-between align-middle m-2 h-[7.76%]"
      >
        <div
          id="synchrotron-assignedcrystals-header-title"
          className="text-lg font-bold w-1/2"
        >
          Assigned Crystals ({crystals?.length})
        </div>
        <div
          id="synchrotron-assignedcrystals-header-unassign-crystal-button"
          className="text-lg font-bold w-1/2 text-right"
        >
          <RMCustomButton
            iconProps={{
              name: rowsSelected ? 'UnassignCrystalActive' : 'UnassignCrystal',
              subfolder: 'SynchrotronTrip',
              fill: '#FFFFFF',
              width: 24,
              height: 24,
            }}
            onClick={rowsSelected ? showUnassignConfirmation : null}
            contentContainerClass={`hover:opacity-70 justify-end w-fit h-fit ml-auto my-auto ${
              rowsSelected ? '!cursor-pointer' : '!cursor-default'
            }`}
            titleTip="Unassign Crystal"
            isDisabled={isReadOnly}
          />
        </div>
      </div>
      <div
        id="synchrotron-assignedcrystals-table"
        className="synch_td overflow-auto h-[92%] p-1"
      >
        <RMTable
          columns={[...tableColumns_]}
          data={crystalList}
          selectedIds={selectedRows}
          onCheckboxSelectionChange={handleSelectionChange}
          onRowClicked={handleOnRowCLick}
          enableMultiSelection
          tableClass="w-full"
        />
      </div>
      <CrystalModal
        showModal={modalDetails.showModal}
        closeModal={() => setModalDetails({ ...modalDetails, showModal: false })}
        body={modalDetails.body}
      />
      <RMModal
        id="synchrotron-assignedcrystals-showunassign-modal"
        showModal={showUnassignModal}
        setShowModal={setShowUnassignModal}
        onOk={handleUnassignCrystal}
        {...modal}
      />
    </>
  );
};

export default AssignedCrystals;
